// apps/web/src/lib/vendas/precos.ts — Fase 12, Plano 06 (PV-09, D-14, R12-10).
// Funções puras da Tabela de Preços: rótulos, validade da prévia de reajuste e acúmulo das páginas da
// prévia presas ao mesmo `previaHash`. Nenhum cálculo de preço acontece aqui (o servidor recalcula).
import type { AlvoReajustePreco, EstadoVersaoPreco, LinhaPreviaReajuste, PreviaReajuste, TipoReajustePreco } from '@pieltec/contracts';

const SINAL_MENOS = '−';

/** Variação percentual com vírgula decimal: `+12,5%`, `−3%`, `0%`. */
export function rotuloVariacao(valor: number): string {
  const absoluto = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Math.abs(valor));
  if (valor > 0) return `+${absoluto}%`;
  if (valor < 0) return `${SINAL_MENOS}${absoluto}%`;
  return '0%';
}

export type IconeEstadoVersao = 'CheckCircle2' | 'CalendarClock' | 'Archive';

const ROTULOS_ESTADO: Record<EstadoVersaoPreco, { texto: string; icone: IconeEstadoVersao }> = {
  VIGENTE: { texto: 'Vigente', icone: 'CheckCircle2' },
  FUTURA: { texto: 'Futura', icone: 'CalendarClock' },
  ENCERRADA: { texto: 'Encerrada', icone: 'Archive' },
};

export function rotuloEstadoVersao(estado: EstadoVersaoPreco): { texto: string; icone: IconeEstadoVersao } {
  return ROTULOS_ESTADO[estado];
}

/** Parâmetros do reajuste que definem a prévia; qualquer mudança nestes campos a invalida. */
export interface ParametrosReajuste {
  readonly alvo: AlvoReajustePreco;
  readonly clienteIds: readonly string[];
  readonly fornecedores: readonly string[];
  readonly tipo: TipoReajustePreco;
  readonly valor: number;
  readonly vigenciaInicio: string;
}

function canonico(lista: readonly string[]): string {
  return JSON.stringify([...new Set(lista)].sort());
}

function mesmosParametros(a: ParametrosReajuste, b: ParametrosReajuste): boolean {
  return (
    a.alvo === b.alvo &&
    a.tipo === b.tipo &&
    a.valor === b.valor &&
    a.vigenciaInicio === b.vigenciaInicio &&
    canonico(a.clienteIds) === canonico(b.clienteIds) &&
    canonico(a.fornecedores) === canonico(b.fornecedores)
  );
}

/** A prévia só vale para exatamente os parâmetros com que foi gerada e enquanto houver hash. */
export function previaValida(entrada: { prevista: ParametrosReajuste | null; atual: ParametrosReajuste; hash: string | null }): boolean {
  if (!entrada.hash || !entrada.prevista) return false;
  return mesmosParametros(entrada.prevista, entrada.atual);
}

export interface EstadoPrevia {
  readonly previaHash: string | null;
  readonly linhas: readonly LinhaPreviaReajuste[];
  readonly total: number;
  readonly ignoradas: number;
  readonly proximoCursor: string | null;
  /** Todas as páginas do mesmo hash carregadas e contagem fechando o total: libera a aplicação. */
  readonly completa: boolean;
}

export const PREVIA_VAZIA: EstadoPrevia = {
  previaHash: null,
  linhas: [],
  total: 0,
  ignoradas: 0,
  proximoCursor: null,
  completa: false,
};

export type ResultadoAcumulo =
  | { readonly ok: true; readonly estado: EstadoPrevia }
  | { readonly ok: false; readonly motivo: 'HASH_DIVERGENTE' | 'INCONSISTENTE' };

/** Soma uma página à prévia: recusa hash diferente, deduplica por chave e só marca completa com a contagem fechada. */
export function acumularPagina(estado: EstadoPrevia, pagina: PreviaReajuste): ResultadoAcumulo {
  if (estado.previaHash !== null && estado.previaHash !== pagina.previaHash) {
    return { ok: false, motivo: 'HASH_DIVERGENTE' };
  }
  const vistas = new Set(estado.linhas.map((linha) => linha.chave));
  const linhas = [...estado.linhas];
  for (const linha of pagina.linhas) {
    if (vistas.has(linha.chave)) continue;
    vistas.add(linha.chave);
    linhas.push(linha);
  }
  const ultima = !pagina.hasNext || pagina.nextCursor === null;
  if (ultima && linhas.length !== pagina.total) {
    return { ok: false, motivo: 'INCONSISTENTE' };
  }
  return {
    ok: true,
    estado: {
      previaHash: pagina.previaHash,
      linhas,
      total: pagina.total,
      ignoradas: pagina.ignoradas,
      proximoCursor: ultima ? null : pagina.nextCursor,
      completa: ultima,
    },
  };
}

/** PREVIA_DESATUALIZADA (ou mudança de campo): descarta linhas, hash e cursor; exige nova prévia. */
export function descartarPrevia(): EstadoPrevia {
  return PREVIA_VAZIA;
}
